import { supabase } from '@/lib/supabase/client';
import type { Component, ComponentInsert, ComponentUpdate } from '@/types/components';

export const componentsService = {
  async getAll(): Promise<Component[]> {
    try {
      const { data, error } = await supabase.from('components').select('*').order('name', { ascending: true });

      if (error) {
        console.error('Error fetching components:', error);
        return [];
      }

      return (data as Component[]) || [];
    } catch (error) {
      console.error('Error fetching components:', error);
      return [];
    }
  },

  async getById(id: string): Promise<Component | null> {
    try {
      const { data, error } = await supabase.from('components').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        console.error('Error fetching component:', error);
        return null;
      }

      return (data as Component) || null;
    } catch (error) {
      console.error('Error fetching component:', error);
      return null;
    }
  },

  async getByType(componentType: Component['component_type']): Promise<Component[]> {
    try {
      const { data, error } = await supabase
        .from('components')
        .select('*')
        .eq('component_type', componentType)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching components by type:', error);
        return [];
      }

      return (data as Component[]) || [];
    } catch (error) {
      console.error('Error fetching components by type:', error);
      return [];
    }
  },

  async create(data: ComponentInsert): Promise<Component> {
    try {
      const insertData = {
        name: data.name,
        sku: data.sku,
        component_type: data.component_type,
        default_vendor: data.default_vendor ?? null,
        default_cost: data.default_cost ?? null,
        description: data.description ?? null,
        has_serial_number: data.has_serial_number ?? false,
        warranty_period_days: data.warranty_period_days ?? null,
      };

      const { data: newComponent, error } = await supabase.from('components').insert(insertData).select().single();

      if (error) {
        console.error('Error creating component:', error);
        throw new Error(`Failed to create component: ${error.message}`);
      }

      return newComponent as Component;
    } catch (error) {
      console.error('Error creating component:', error);
      throw error;
    }
  },

  async update(id: string, data: ComponentUpdate): Promise<Component> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.name !== undefined) updateData.name = data.name;
      if (data.sku !== undefined) updateData.sku = data.sku;
      if (data.component_type !== undefined) updateData.component_type = data.component_type;
      if (data.default_vendor !== undefined) updateData.default_vendor = data.default_vendor;
      if (data.default_cost !== undefined) updateData.default_cost = data.default_cost;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.has_serial_number !== undefined) updateData.has_serial_number = data.has_serial_number;
      if (data.warranty_period_days !== undefined) updateData.warranty_period_days = data.warranty_period_days;

      const { data: updatedComponent, error } = await supabase
        .from('components')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating component:', error);
        throw new Error(`Failed to update component: ${error.message}`);
      }

      if (!updatedComponent) {
        throw new Error('Component not found');
      }

      return updatedComponent as Component;
    } catch (error) {
      console.error('Error updating component:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('components').delete().eq('id', id);

      if (error) {
        console.error('Error deleting component:', error);
        throw new Error(`Failed to delete component: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting component:', error);
      throw error;
    }
  },
};

